import React, { useMemo, useState } from 'react';
import Head from 'next/head';
import { ModalLightbox, useModal } from '@blueprint/ui';
import {
    PageGalleryContainer,
    PageGalleryContent,
    ModalGalleryContainer
} from './ListingGallery.styles';
import Gallery from 'shared/components/Gallery/Gallery';
import {
    trackImageClick,
    trackNextImage,
    trackPreviousImage,
    trackZoomImage,
    trackModalNextImage,
    trackModalPreviousImage,
    trackModalZoomImage,
    trackModalClose
} from './ListingGallery.tracking';

import { mapPropertyImageResponseToGalleryImages } from 'listing-details/helpers';
import { ListingGalleryProps } from './ListingGallery.types';
import {
    responsiveSizesPage,
    responsiveSizesLightbox
} from './ListingGallery.constants';
import { generatePrimaryImageSrcset } from './ListingGallery.helpers';

const ListingGallery = ({ data }: ListingGalleryProps) => {
    const [clickedOnSlideIndex, setClickedOnSlideIndex] = useState(0);

    const [
        isShowingGalleryModal,
        openGalleryModal,
        closeGalleryModal
    ] = useModal({
        onClose: trackModalClose
    });

    const imagesPage = useMemo(
        () =>
            mapPropertyImageResponseToGalleryImages(
                responsiveSizesPage,
                data.propertyImage || [],
                ['webp']
            ),
        [data.propertyImage]
    );

    const imagesLightbox = useMemo(
        () =>
            mapPropertyImageResponseToGalleryImages(
                responsiveSizesLightbox,
                data.propertyImage || [],
                ['webp']
            ),
        [data.propertyImage]
    );

    const primaryImage = imagesPage[0]?.responsiveImgList?.webp
        ? imagesPage[0]?.responsiveImgList?.webp
        : imagesPage[0]?.responsiveImgList?.original;

    const primaryImageSrcset = generatePrimaryImageSrcset(primaryImage);

    return (
        <>
            {Boolean(imagesPage.length) && (
                <Head>
                    <link
                        rel="preload"
                        as="image"
                        href={imagesPage[0]?.src}
                        // These attributes are not yet typed in our combo of typescript/react
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        imagesrcset={primaryImageSrcset}
                        imagesizes={responsiveSizesPage}
                    />
                </Head>
            )}

            <PageGalleryContainer>
                <PageGalleryContent>
                    <Gallery
                        images={imagesPage}
                        allowCropping={true}
                        createCaption={(index, total) =>
                            `Property photo ${index + 1} of ${total}. ${
                                imagesPage[index].alt
                            }`
                        }
                        onSlideClick={(slideIndex) => {
                            trackImageClick(slideIndex);
                            setClickedOnSlideIndex(slideIndex);
                            openGalleryModal(null);
                        }}
                        onSlideNext={trackNextImage}
                        onSlidePrevious={trackPreviousImage}
                        onSlideZoom={trackZoomImage}
                        hasKeyboardControls={false}
                        hasArrowsOnImage={true}
                    />
                </PageGalleryContent>
            </PageGalleryContainer>

            <ModalLightbox
                isShowing={isShowingGalleryModal}
                close={closeGalleryModal}
                title="Gallery"
            >
                <ModalGalleryContainer>
                    <Gallery
                        images={imagesLightbox}
                        initialSlide={clickedOnSlideIndex}
                        hasZoomControls={true}
                        createCaption={(index, total) =>
                            `Property photo ${index + 1} of ${total}. ${
                                imagesLightbox[index].alt
                            }`
                        }
                        onSlideNext={trackModalNextImage}
                        onSlidePrevious={trackModalPreviousImage}
                        onSlideZoom={trackModalZoomImage}
                    />
                </ModalGalleryContainer>
            </ModalLightbox>
        </>
    );
};

export default ListingGallery;
